import React, { useState, useEffect } from 'react';
import { X, Mail, Lock, User, Loader2, Eye, EyeOff, ArrowLeft, CheckCircle, ShieldCheck } from 'lucide-react';
import { Button } from './ui/button';
import { useAuth } from '../contexts/AuthContext';
import { useToast } from '../hooks/use-toast';
import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const AuthModal = ({ isOpen, onClose, defaultTab = 'login' }) => {
  const [activeTab, setActiveTab] = useState(defaultTab);
  const [showForgotPassword, setShowForgotPassword] = useState(false);
  const [forgotEmail, setForgotEmail] = useState('');
  const [forgotLoading, setForgotLoading] = useState(false);
  const [forgotSent, setForgotSent] = useState(false);
  
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [name, setName] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [loading, setLoading] = useState(false);

  // 2FA states
  const [show2fa, setShow2fa] = useState(false);
  const [totpCode, setTotpCode] = useState('');
  const [show2faSetup, setShow2faSetup] = useState(false);
  const [qrCode, setQrCode] = useState('');
  const [setupSecret, setSetupSecret] = useState('');
  const [setupCode, setSetupCode] = useState('');
  const [setupLoading, setSetupLoading] = useState(false);

  // Email verification state
  const [showVerificationMsg, setShowVerificationMsg] = useState(false);

  const { login, register, token } = useAuth();
  const { toast } = useToast();

  useEffect(() => {
    if (isOpen) {
      setActiveTab(defaultTab);
      setShowForgotPassword(false);
      setForgotSent(false);
      setShow2fa(false);
      setTotpCode('');
      setShow2faSetup(false);
      setShowVerificationMsg(false);
    }
  }, [defaultTab, isOpen]);

  if (!isOpen) return null;

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);

    try {
      if (activeTab === 'login') {
        const result = await login(email, password, totpCode);
        if (result.requires_2fa) {
          setShow2fa(true);
          setLoading(false);
          return;
        }
        if (result.requires_2fa_setup) {
          // Admin needs to setup 2FA - trigger setup flow
          await start2faSetup();
          setLoading(false);
          return;
        }
        toast({ title: 'Connexion reussie !', description: 'Bienvenue sur SENDBOX' });
      } else {
        const result = await register(email, password, name);
        if (result?.requires_verification) {
          setShowVerificationMsg(true);
          setLoading(false);
          return;
        }
        toast({ title: 'Compte cree !', description: 'Bienvenue sur SENDBOX' });
      }
      onClose();
    } catch (error) {
      toast({
        title: 'Erreur',
        description: error.response?.data?.detail || 'Une erreur est survenue',
        variant: 'destructive',
      });
      if (show2fa) setTotpCode('');
    } finally {
      setLoading(false);
    }
  };

  const start2faSetup = async () => {
    try {
      const tokenVal = localStorage.getItem('sendbox_token');
      const res = await axios.post(`${API}/auth/2fa/setup`, {}, {
        headers: { Authorization: `Bearer ${tokenVal}` }
      });
      setQrCode(res.data.qr_code);
      setSetupSecret(res.data.secret);
      setShow2faSetup(true);
    } catch (e) {
      toast({ title: 'Erreur', description: 'Impossible de configurer la 2FA', variant: 'destructive' });
    }
  };

  const handle2faSetupVerify = async () => {
    setSetupLoading(true);
    try {
      const tokenVal = localStorage.getItem('sendbox_token');
      await axios.post(`${API}/auth/2fa/verify-setup`, { code: setupCode }, {
        headers: { Authorization: `Bearer ${tokenVal}` }
      });
      toast({ title: 'Double authentification activée !', description: 'Votre compte est maintenant sécurisé.' });
      setShow2faSetup(false);
      onClose();
    } catch (e) {
      toast({ title: 'Erreur', description: e.response?.data?.detail || 'Code invalide', variant: 'destructive' });
    } finally {
      setSetupLoading(false);
    }
  };

  const handleForgotPassword = async (e) => {
    e.preventDefault();
    if (!forgotEmail) return;
    setForgotLoading(true);
    try {
      await axios.post(`${API}/auth/forgot-password`, { email: forgotEmail });
      setForgotSent(true);
    } catch (error) {
      setForgotSent(true);
    } finally {
      setForgotLoading(false);
    }
  };

  // Email verification message view
  if (showVerificationMsg) {
    return (
      <div className="fixed inset-0 bg-black/70 backdrop-blur-md flex items-center justify-center z-50 p-4"
        onClick={(e) => { if (e.target === e.currentTarget) { onClose(); setShowVerificationMsg(false); } }}>
        <div className="bg-slate-900 border border-blue-500/30 rounded-2xl shadow-2xl max-w-md w-full animate-in zoom-in-95">
          <div className="flex items-center justify-between p-6 border-b border-blue-500/20">
            <h2 className="text-xl font-semibold text-white flex items-center gap-2">
              <Mail className="w-5 h-5 text-cyan-400" />
              Vérifiez votre email
            </h2>
            <button onClick={() => { onClose(); setShowVerificationMsg(false); }} className="text-blue-300/50 hover:text-white transition-colors">
              <X className="w-5 h-5" />
            </button>
          </div>
          <div className="p-6 space-y-4 text-center">
            <div className="w-16 h-16 mx-auto bg-cyan-500/20 rounded-full flex items-center justify-center">
              <Mail className="w-8 h-8 text-cyan-400" />
            </div>
            <h3 className="text-lg font-semibold text-white">Email de confirmation envoyé !</h3>
            <p className="text-blue-300/70 text-sm">
              Un email de confirmation a été envoyé à <strong className="text-white">{email}</strong>. 
              Cliquez sur le lien dans l'email pour activer votre compte.
            </p>
            <div className="bg-slate-800/50 border border-blue-500/20 rounded-lg p-4">
              <p className="text-blue-300/60 text-xs">
                Vous ne trouvez pas l'email ? Vérifiez votre dossier spam ou 
                <button onClick={async () => {
                  try {
                    await register(email, password, name);
                    toast({ title: 'Email renvoyé', description: 'Un nouvel email de confirmation a été envoyé' });
                  } catch (err) {
                    toast({ title: 'Erreur', description: 'Impossible de renvoyer l\'email', variant: 'destructive' });
                  }
                }} className="text-cyan-400 hover:underline ml-1" data-testid="resend-verification-btn">
                  renvoyez-le
                </button>
              </p>
            </div>
            <Button onClick={() => { setShowVerificationMsg(false); setActiveTab('login'); }} 
              className="w-full bg-gradient-to-r from-blue-500 to-cyan-500 hover:from-blue-600 hover:to-cyan-600 text-white font-semibold py-5"
              data-testid="go-to-login-btn">
              J'ai confirmé, me connecter
            </Button>
          </div>
        </div>
      </div>
    );
  }

  // 2FA Setup View (forced for admins)
  if (show2faSetup) {
    return (
      <div className="fixed inset-0 bg-black/70 backdrop-blur-md flex items-center justify-center z-50 p-4"
        onClick={(e) => e.target === e.currentTarget}>
        <div className="bg-slate-900 border border-blue-500/30 rounded-2xl shadow-2xl max-w-md w-full animate-in zoom-in-95">
          <div className="flex items-center justify-between p-6 border-b border-blue-500/20">
            <h2 className="text-xl font-semibold text-white flex items-center gap-2">
              <ShieldCheck className="w-5 h-5 text-emerald-400" />
              Configuration 2FA obligatoire
            </h2>
          </div>
          <div className="p-6 space-y-4">
            <p className="text-blue-300/70 text-sm">
              En tant qu'administrateur, la double authentification est obligatoire. Scannez le QR code avec votre application d'authentification (Google Authenticator, Authy, etc.).
            </p>
            {qrCode && (
              <div className="flex justify-center">
                <div className="bg-white p-3 rounded-xl">
                  <img src={qrCode} alt="QR Code 2FA" className="w-48 h-48" data-testid="2fa-qr-code" />
                </div>
              </div>
            )}
            <div className="bg-slate-800/50 rounded-lg p-3">
              <p className="text-xs text-blue-300/50 mb-1">Clé secrète (saisie manuelle) :</p>
              <code className="text-sm text-emerald-400 font-mono break-all" data-testid="2fa-secret">{setupSecret}</code>
            </div>
            <div>
              <label className="block text-sm text-blue-300 mb-2">Code de vérification</label>
              <input
                type="text"
                inputMode="numeric"
                maxLength={6}
                value={setupCode}
                onChange={(e) => setSetupCode(e.target.value.replace(/\D/g, ''))}
                placeholder="000000"
                className="w-full bg-slate-800/50 border border-blue-500/30 rounded-lg px-4 py-3 text-white text-center text-2xl tracking-[0.5em] font-mono placeholder-blue-300/30 focus:outline-none focus:border-emerald-400"
                data-testid="2fa-setup-code-input"
              />
            </div>
            <Button
              onClick={handle2faSetupVerify}
              disabled={setupLoading || setupCode.length !== 6}
              className="w-full bg-emerald-600 hover:bg-emerald-700 text-white font-semibold py-6"
              data-testid="2fa-setup-verify-btn"
            >
              {setupLoading ? (
                <span className="flex items-center gap-2"><Loader2 className="w-5 h-5 animate-spin" /> Vérification...</span>
              ) : 'Activer la double authentification'}
            </Button>
          </div>
        </div>
      </div>
    );
  }

  // 2FA Code Entry View
  if (show2fa) {
    return (
      <div className="fixed inset-0 bg-black/70 backdrop-blur-md flex items-center justify-center z-50 p-4"
        onClick={(e) => e.target === e.currentTarget && onClose()}>
        <div className="bg-slate-900 border border-blue-500/30 rounded-2xl shadow-2xl max-w-md w-full animate-in zoom-in-95">
          <div className="flex items-center justify-between p-6 border-b border-blue-500/20">
            <button onClick={() => { setShow2fa(false); setTotpCode(''); }}
              className="text-blue-300/60 hover:text-white transition-colors">
              <ArrowLeft className="w-5 h-5" />
            </button>
            <h2 className="text-xl font-semibold text-white flex items-center gap-2">
              <ShieldCheck className="w-5 h-5 text-emerald-400" />
              Vérification 2FA
            </h2>
            <button onClick={onClose} className="text-blue-300/60 hover:text-white transition-colors">
              <X className="w-5 h-5" />
            </button>
          </div>
          <form onSubmit={handleSubmit} className="p-6 space-y-4">
            <p className="text-blue-300/70 text-sm text-center">
              Entrez le code à 6 chiffres de votre application d'authentification.
            </p>
            <input
              type="text"
              inputMode="numeric"
              maxLength={6}
              value={totpCode}
              onChange={(e) => setTotpCode(e.target.value.replace(/\D/g, ''))}
              placeholder="000000"
              autoFocus
              className="w-full bg-slate-800/50 border border-blue-500/30 rounded-lg px-4 py-4 text-white text-center text-3xl tracking-[0.5em] font-mono placeholder-blue-300/30 focus:outline-none focus:border-emerald-400"
              data-testid="2fa-code-input"
            />
            <Button
              type="submit"
              disabled={loading || totpCode.length !== 6}
              className="w-full bg-gradient-to-r from-blue-500 to-cyan-500 hover:from-blue-600 hover:to-cyan-600 text-white font-semibold py-6"
              data-testid="2fa-verify-btn"
            >
              {loading ? (
                <span className="flex items-center gap-2"><Loader2 className="w-5 h-5 animate-spin" /> Vérification...</span>
              ) : 'Vérifier'}
            </Button>
          </form>
        </div>
      </div>
    );
  }

  // Forgot Password View
  if (showForgotPassword) {
    return (
      <div 
        className="fixed inset-0 bg-black/70 backdrop-blur-md flex items-center justify-center z-50 p-4"
        onClick={(e) => e.target === e.currentTarget && onClose()}
      >
        <div className="bg-slate-900 border border-blue-500/30 rounded-2xl shadow-2xl max-w-md w-full animate-in zoom-in-95">
          <div className="flex items-center justify-between p-6 border-b border-blue-500/20">
            <button
              onClick={() => { setShowForgotPassword(false); setForgotSent(false); }}
              className="text-blue-300/60 hover:text-white transition-colors"
            >
              <ArrowLeft className="w-5 h-5" />
            </button>
            <h2 className="text-xl font-semibold text-white">Mot de passe oublie</h2>
            <button
              onClick={onClose}
              className="text-blue-300/60 hover:text-white transition-colors"
            >
              <X className="w-5 h-5" />
            </button>
          </div>

          <div className="p-6">
            {forgotSent ? (
              <div className="text-center space-y-4">
                <div className="w-16 h-16 mx-auto bg-green-500/20 rounded-full flex items-center justify-center">
                  <CheckCircle className="w-8 h-8 text-green-400" />
                </div>
                <h3 className="text-lg font-medium text-white">Email envoye !</h3>
                <p className="text-blue-300/60 text-sm">
                  Si un compte existe avec cet email, vous recevrez un lien pour reinitialiser votre mot de passe.
                </p>
                <Button
                  onClick={() => { setShowForgotPassword(false); setForgotSent(false); setForgotEmail(''); }}
                  className="w-full bg-gradient-to-r from-blue-500 to-cyan-500 text-white"
                >
                  Retour a la connexion
                </Button>
              </div>
            ) : (
              <form onSubmit={handleForgotPassword} className="space-y-4">
                <p className="text-blue-300/60 text-sm">
                  Entrez votre adresse email et nous vous enverrons un lien pour reinitialiser votre mot de passe.
                </p>
                <div>
                  <label className="block text-sm text-blue-300 mb-2">Email</label>
                  <div className="relative">
                    <Mail className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-blue-400/50" />
                    <input
                      type="email"
                      value={forgotEmail}
                      onChange={(e) => setForgotEmail(e.target.value)}
                      placeholder="votre@email.com"
                      required
                      className="w-full bg-slate-800/50 border border-blue-500/30 rounded-lg pl-10 pr-4 py-3 text-white placeholder-blue-300/40 focus:outline-none focus:border-blue-400"
                    />
                  </div>
                </div>
                <Button
                  type="submit"
                  disabled={forgotLoading || !forgotEmail}
                  className="w-full bg-gradient-to-r from-blue-500 to-cyan-500 hover:from-blue-600 hover:to-cyan-600 text-white font-semibold py-6"
                >
                  {forgotLoading ? (
                    <span className="flex items-center gap-2">
                      <Loader2 className="w-5 h-5 animate-spin" />
                      Envoi en cours...
                    </span>
                  ) : (
                    'Envoyer le lien de reinitialisation'
                  )}
                </Button>
              </form>
            )}
          </div>
        </div>
      </div>
    );
  }

  // Main Login/Register View
  return (
    <div 
      className="fixed inset-0 bg-black/70 backdrop-blur-md flex items-center justify-center z-50 p-4"
      onClick={(e) => e.target === e.currentTarget && onClose()}
    >
      <div className="bg-slate-900 border border-blue-500/30 rounded-2xl shadow-2xl max-w-md w-full animate-in zoom-in-95">
        {/* Header */}
        <div className="flex items-center justify-between p-6 border-b border-blue-500/20">
          <h2 className="text-xl font-semibold text-white">
            {activeTab === 'login' ? 'Connexion' : 'Creer un compte'}
          </h2>
          <button
            onClick={onClose}
            className="text-blue-300/60 hover:text-white transition-colors"
          >
            <X className="w-5 h-5" />
          </button>
        </div>

        {/* Tabs */}
        <div className="flex border-b border-blue-500/20">
          <button
            onClick={() => setActiveTab('login')}
            className={`flex-1 py-3 text-sm font-medium transition-colors ${
              activeTab === 'login'
                ? 'text-white border-b-2 border-blue-500'
                : 'text-blue-300/60 hover:text-white'
            }`}
          >
            Connexion
          </button>
          <button
            onClick={() => setActiveTab('register')}
            className={`flex-1 py-3 text-sm font-medium transition-colors ${
              activeTab === 'register'
                ? 'text-white border-b-2 border-blue-500'
                : 'text-blue-300/60 hover:text-white'
            }`}
          >
            Inscription
          </button>
        </div>

        {/* Form */}
        <form onSubmit={handleSubmit} className="p-6 space-y-4">
          {activeTab === 'register' && (
            <div>
              <label className="block text-sm text-blue-300 mb-2">Nom</label>
              <div className="relative">
                <User className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-blue-400/50" />
                <input
                  type="text"
                  value={name}
                  onChange={(e) => setName(e.target.value)}
                  placeholder="Votre nom"
                  className="w-full bg-slate-800/50 border border-blue-500/30 rounded-lg pl-10 pr-4 py-3 text-white placeholder-blue-300/40 focus:outline-none focus:border-blue-400"
                />
              </div>
            </div>
          )}

          <div>
            <label className="block text-sm text-blue-300 mb-2">Email</label>
            <div className="relative">
              <Mail className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-blue-400/50" />
              <input
                type="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                placeholder="votre@email.com"
                required
                className="w-full bg-slate-800/50 border border-blue-500/30 rounded-lg pl-10 pr-4 py-3 text-white placeholder-blue-300/40 focus:outline-none focus:border-blue-400"
              />
            </div>
          </div>

          <div>
            <label className="block text-sm text-blue-300 mb-2">Mot de passe</label>
            <div className="relative">
              <Lock className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-blue-400/50" />
              <input
                type={showPassword ? 'text' : 'password'}
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                placeholder="********"
                required
                minLength={6}
                className="w-full bg-slate-800/50 border border-blue-500/30 rounded-lg pl-10 pr-12 py-3 text-white placeholder-blue-300/40 focus:outline-none focus:border-blue-400"
              />
              <button
                type="button"
                onClick={() => setShowPassword(!showPassword)}
                className="absolute right-3 top-1/2 -translate-y-1/2 text-blue-400/50 hover:text-blue-300"
              >
                {showPassword ? <EyeOff className="w-5 h-5" /> : <Eye className="w-5 h-5" />}
              </button>
            </div>
            {activeTab === 'register' && (
              <p className="text-xs text-blue-300/50 mt-1">Minimum 6 caracteres</p>
            )}
          </div>

          <Button
            type="submit"
            disabled={loading}
            className="w-full bg-gradient-to-r from-blue-500 to-cyan-500 hover:from-blue-600 hover:to-cyan-600 text-white font-semibold py-6"
          >
            {loading ? (
              <span className="flex items-center gap-2">
                <Loader2 className="w-5 h-5 animate-spin" />
                {activeTab === 'login' ? 'Connexion...' : 'Creation...'}
              </span>
            ) : (
              activeTab === 'login' ? 'Se connecter' : 'Creer mon compte'
            )}
          </Button>

          {activeTab === 'login' && (
            <div className="text-center space-y-2">
              <button
                type="button"
                onClick={() => setShowForgotPassword(true)}
                className="text-sm text-blue-400 hover:text-blue-300"
              >
                Mot de passe oublie ?
              </button>
              <p className="text-sm text-blue-300/60">
                Pas encore de compte ?{' '}
                <button
                  type="button"
                  onClick={() => setActiveTab('register')}
                  className="text-blue-400 hover:text-blue-300"
                >
                  S'inscrire
                </button>
              </p>
            </div>
          )}

          {activeTab === 'register' && (
            <p className="text-center text-sm text-blue-300/60">
              Deja un compte ?{' '}
              <button
                type="button"
                onClick={() => setActiveTab('login')}
                className="text-blue-400 hover:text-blue-300"
              >
                Se connecter
              </button>
            </p>
          )}
        </form>
      </div>
    </div>
  );
};

export default AuthModal;
